import { useCallback, useEffect, useMemo, useState } from 'react';
import { FiAlertCircle, FiCalendar, FiCheckCircle, FiCreditCard, FiEye, FiRefreshCw, FiSearch, FiXCircle } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { SearchableSelect } from '../../components/ui';
import { adminApi, type AdminPlan, type AdminSubscription } from '../../services/admin';
import styles from './Admin.module.css';
import { AdminLayout } from './AdminLayout';

export function AdminSubscriptions() {
    const navigate = useNavigate();
    const [subs, setSubs] = useState<AdminSubscription[]>([]);
    const [plans, setPlans] = useState<AdminPlan[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [renewModal, setRenewModal] = useState<AdminSubscription | null>(null);
    const [planModal, setPlanModal] = useState<AdminSubscription | null>(null);
    const [renewDate, setRenewDate] = useState('');
    const [selectedPlanId, setSelectedPlanId] = useState('');
    const [saving, setSaving] = useState(false);
    const [modalError, setModalError] = useState<string | null>(null);
    const [actionError, setActionError] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [s, p] = await Promise.all([adminApi.listSubscriptions(), adminApi.listPlans()]);
            setSubs(s);
            setPlans(p);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const filtered = useMemo(() =>
        subs.filter(s =>
            s.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            s.userEmail.toLowerCase().includes(searchTerm.toLowerCase())
        ), [subs, searchTerm]);

    const statusLabel: Record<string, string> = {
        ACTIVE: 'Ativa', TRIAL: 'Trial', PAST_DUE: 'Em atraso',
        CANCELED: 'Cancelada', SUSPENDED: 'Suspensa',
    };

    const formatDate = (d?: string) => d ? new Date(d).toLocaleDateString('pt-BR') : '-';
    const isExpired = (d?: string) => d ? new Date(d) < new Date() : false;
    const isExpiringSoon = (d?: string) => {
        if (!d) return false;
        const diff = new Date(d).getTime() - Date.now();
        return diff > 0 && diff < 30 * 86400000;
    };

    const handleRenew = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!renewModal) return;
        setSaving(true);
        try {
            await adminApi.renewSubscription(renewModal.userId, { expiresAt: renewDate });
            setRenewModal(null);
            load();
        } catch (err: any) { setModalError(err.message); }
        finally { setSaving(false); }
    };

    const handleAssignPlan = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!planModal) return;
        setSaving(true);
        try {
            await adminApi.assignPlan(planModal.userId, { planId: selectedPlanId });
            setPlanModal(null);
            load();
        } catch (err: any) { setModalError(err.message); }
        finally { setSaving(false); }
    };

    const handleCancel = async (sub: AdminSubscription) => {
        if (!confirm(`Cancelar assinatura de ${sub.userName}?`)) return;
        try {
            setActionError(null);
            await adminApi.cancelSubscription(sub.userId);
            load();
        } catch (err: any) { setActionError(err.message); }
    };

    const handleSuspend = async (sub: AdminSubscription) => {
        if (!confirm(`Suspender assinatura de ${sub.userName}?`)) return;
        try {
            setActionError(null);
            await adminApi.suspendSubscription(sub.userId);
            load();
        } catch (err: any) { setActionError(err.message); }
    };

    const handleReactivate = async (sub: AdminSubscription) => {
        try {
            setActionError(null);
            await adminApi.reactivateSubscription(sub.userId);
            load();
        } catch (err: any) { setActionError(err.message); }
    };

    return (
        <AdminLayout>
            <div className={styles.pageHeader}>
                <div className={styles.headerLeft}>
                    <h2>Assinaturas</h2>
                    <p>Gerencie vencimentos, planos e status de assinaturas</p>
                </div>
            </div>

            <div className={styles.filtersRow}>
                <div className={styles.searchInput}>
                    <FiSearch size={16} className={styles.searchIcon} />
                    <input type="text" placeholder="Buscar por nome ou email..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                </div>
            </div>

            {actionError && <p className={styles.formError}>{actionError}</p>}

            {loading ? (
                <div className={styles.loadingState}>Carregando...</div>
            ) : (
                <div className={styles.usersSection}>
                    <table className={styles.usersTable}>
                        <thead>
                            <tr>
                                <th>Usuário</th>
                                <th>Plano</th>
                                <th>Status</th>
                                <th>Início</th>
                                <th>Vencimento</th>
                                <th>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map(sub => (
                                <tr key={sub.id}>
                                    <td>
                                        <div className={styles.userInfo}>
                                            <div className={styles.userAvatar}>{sub.userName.charAt(0).toUpperCase()}</div>
                                            <div className={styles.userDetails}>
                                                <div className={styles.name}>{sub.userName}</div>
                                                <div className={styles.email}>{sub.userEmail}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td><span className={styles.planBadge}>{sub.planName}</span></td>
                                    <td>
                                        <span className={`${styles.statusBadge} ${styles[sub.status.toLowerCase()]}`}>
                                            {statusLabel[sub.status] ?? sub.status}
                                        </span>
                                    </td>
                                    <td>{formatDate(sub.startedAt)}</td>
                                    <td>
                                        {sub.currentPeriodEnd ? (
                                            <div className={styles.expiryCell}>
                                                <span className={`${styles.expiryDate} ${isExpired(sub.currentPeriodEnd) ? styles.expired : isExpiringSoon(sub.currentPeriodEnd) ? styles.expiringSoon : ''}`}>
                                                    {formatDate(sub.currentPeriodEnd)}
                                                </span>
                                                {isExpired(sub.currentPeriodEnd) && <FiAlertCircle size={14} className={styles.expiredIcon} />}
                                                {isExpiringSoon(sub.currentPeriodEnd) && !isExpired(sub.currentPeriodEnd) && <FiCalendar size={14} className={styles.expiringSoonIcon} />}
                                            </div>
                                        ) : <span className={styles.noExpiry}>-</span>}
                                    </td>
                                    <td>
                                        <div className={styles.actionButtons}>
                                            <button className={styles.actionBtn} title="Ver cliente" onClick={() => navigate(`/admin/clientes/${sub.userId}`)}>
                                                <FiEye size={16} />
                                            </button>
                                            <button className={`${styles.actionBtn} ${styles.renewBtn}`} title="Renovar" onClick={() => { setRenewDate(''); setRenewModal(sub); setModalError(null); }}>
                                                <FiRefreshCw size={16} />
                                            </button>
                                            <button className={styles.actionBtn} title="Alterar plano" onClick={() => { setSelectedPlanId(sub.planId); setPlanModal(sub); setModalError(null); }}>
                                                <FiCreditCard size={16} />
                                            </button>
                                            {sub.status === 'SUSPENDED' ? (
                                                <button className={`${styles.actionBtn} ${styles.activateBtn}`} title="Reativar" onClick={() => handleReactivate(sub)}>
                                                    <FiCheckCircle size={16} />
                                                </button>
                                            ) : (
                                                <button className={`${styles.actionBtn} ${styles.suspendBtn}`} title="Suspender" onClick={() => handleSuspend(sub)}>
                                                    <FiAlertCircle size={16} />
                                                </button>
                                            )}
                                            {sub.status !== 'CANCELED' && (
                                                <button className={`${styles.actionBtn} ${styles.deleteBtn}`} title="Cancelar" onClick={() => handleCancel(sub)}>
                                                    <FiXCircle size={16} />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Modal renovar */}
            <Modal isOpen={!!renewModal} onClose={() => { setRenewModal(null); setModalError(null); }} title={`Renovar: ${renewModal?.userName}`}>
                <form onSubmit={handleRenew}>
                    <div className={styles.formGroup}>
                        <Input label="Nova data de vencimento" type="date" value={renewDate} onChange={e => setRenewDate(e.target.value)} />
                    </div>
                    {modalError && <p className={styles.formError}>{modalError}</p>}
                    <div className={styles.modalFooter}>
                        <Button type="button" onClick={() => { setRenewModal(null); setModalError(null); }}>Cancelar</Button>
                        <Button type="submit" disabled={saving}>{saving ? 'Salvando...' : 'Renovar'}</Button>
                    </div>
                </form>
            </Modal>

            {/* Modal alterar plano */}
            <Modal isOpen={!!planModal} onClose={() => { setPlanModal(null); setModalError(null); }} title={`Alterar Plano: ${planModal?.userName}`}>
                <form onSubmit={handleAssignPlan}>
                    <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Plano</label>
                        <SearchableSelect
                            className={styles.formSelect}
                            value={selectedPlanId}
                            onChange={setSelectedPlanId}
                            emptyLabel="Sem plano"
                            options={plans.map(p => ({ value: p.id, label: `${p.name} — R$ ${Number(p.price).toFixed(2)}/${p.interval === 'yearly' ? 'ano' : 'mês'}` }))}
                            fullWidth
                        />
                    </div>
                    {modalError && <p className={styles.formError}>{modalError}</p>}
                    <div className={styles.modalFooter}>
                        <Button type="button" onClick={() => { setPlanModal(null); setModalError(null); }}>Cancelar</Button>
                        <Button type="submit" disabled={saving}>{saving ? 'Salvando...' : 'Confirmar'}</Button>
                    </div>
                </form>
            </Modal>
        </AdminLayout>
    );
}
